import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, CreditCard, PiggyBank, AlertCircle, Check, X } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета накоплений на образование
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматирование процентов для отображения
  const formatPercent = (value) => {
    return `${formatNumber(value * 100, 1)}%`;
  };

  return (
    <ToolResult title="Результаты расчета накоплений на образование">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Накопления */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <PiggyBank className="w-4 h-4"/>
            Итоговая сумма накоплений
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatCurrency(results.finalSavingsAmount)}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Ваши текущие накопления и ежемесячные взносы с учетом инвестиционного дохода
          </p>
        </div>

        {/* Стоимость образования */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Полная стоимость образования
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatCurrency(results.totalEducationCost)}
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            С учетом обучения, проживания и дополнительных расходов, за вычетом стипендии
          </p>
        </div>
      </div>

      {/* Статус соответствия накоплений */}
      <div className={`mb-6 p-4 rounded-lg border ${results.isEnough 
        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            {results.isEnough 
              ? <Check className="w-5 h-5 text-green-500" /> 
              : <X className="w-5 h-5 text-red-500" />}
          </div>
          <div>
            <h4 className={`text-base font-medium mb-1 ${results.isEnough 
              ? 'text-green-800 dark:text-green-300' 
              : 'text-red-800 dark:text-red-300'}`}>
              {results.isEnough 
                ? 'Накоплений достаточно для обучения!' 
                : 'Накоплений недостаточно для обучения'}
            </h4>
            <p className={`text-sm ${results.isEnough 
              ? 'text-green-600 dark:text-green-400' 
              : 'text-red-600 dark:text-red-400'}`}>
              {results.isEnough 
                ? `Профицит средств составляет ${formatCurrency(results.savingsGap)}. Вы можете уменьшить размер ежемесячных взносов или рассмотреть более престижные учебные заведения.` 
                : `Необходимо накопить дополнительно ${formatCurrency(Math.abs(results.savingsGap))}. Рекомендуемый ежемесячный взнос: ${formatCurrency(results.adjustedMonthlyContribution)}.`}
            </p>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Лет до начала обучения" 
          value={`${results.yearsUntilStart} ${formatYearsLabel(results.yearsUntilStart)}`} 
          color="blue" 
          icon={<Clock className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Длительность обучения" 
          value={`${results.educationDuration} ${formatYearsLabel(results.educationDuration)}`} 
          color="blue" 
          icon={<CalendarDays className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Ожидаемая инфляция" 
          value={`${results.annualInflationRate * 100}% годовых`} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Стоимость обучения сейчас" 
          value={`${formatCurrency(results.currentTuitionPerYear)} в год`} 
          color="orange" 
        />
        
        <ResultItem 
          label="Стоимость обучения к началу обучения" 
          value={`${formatCurrency(results.futureTuitionPerYear)} в год`} 
          color="orange" 
        />
        
        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Структура стоимости образования:</h4>
          
          <ResultItem 
            label="Полная стоимость обучения" 
            value={formatCurrency(results.tuitionCost)} 
            color="orange" 
          />
          
          <ResultItem 
            label="Расходы на проживание" 
            value={formatCurrency(results.livingExpensesCost)} 
          />
          
          <ResultItem 
            label="Учебники и материалы" 
            value={formatCurrency(results.booksAndSuppliesCost)} 
          />
          
          {results.extraExpensesCost > 0 && (
            <ResultItem 
              label="Дополнительные расходы" 
              value={formatCurrency(results.extraExpensesCost)} 
            />
          )}
          
          {results.scholarshipBenefit > 0 && (
            <ResultItem 
              label="Стипендия (вычитается)" 
              value={`-${formatCurrency(results.scholarshipBenefit)}`} 
              color="green" 
            />
          )}
        </div>
        
        {(results.maternityCapitalBenefit > 0 || results.regionalSupportBenefit > 0 || results.taxDeductionBenefit > 0) && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Государственная поддержка и вычеты:</h4>
            
            {results.maternityCapitalBenefit > 0 && (
              <ResultItem 
                label="Материнский капитал" 
                value={formatCurrency(results.maternityCapitalBenefit)} 
                color="green" 
              />
            )}
            
            {results.regionalSupportBenefit > 0 && (
              <ResultItem 
                label="Региональная поддержка" 
                value={formatCurrency(results.regionalSupportBenefit)} 
                color="green" 
              />
            )}
            
            {results.taxDeductionBenefit > 0 && (
              <ResultItem 
                label="Налоговый вычет" 
                value={formatCurrency(results.taxDeductionBenefit)} 
                color="green" 
              />
            )}
          </div>
        )}
        
        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Накопления:</h4>
          
          <ResultItem 
            label="Текущие накопления" 
            value={formatCurrency(results.currentSavings)} 
            color="blue" 
          />
          
          <ResultItem 
            label="Ежемесячный взнос" 
            value={formatCurrency(results.monthlyContribution)} 
            color="blue" 
          />
          
          <ResultItem 
            label="Сумма всех взносов" 
            value={formatCurrency(results.totalContributions)} 
          />
          
          <ResultItem 
            label="Инвестиционный доход" 
            value={formatCurrency(results.expectedReturns)} 
            color="green" 
          />
        </div>

        {/* Итоговые результаты */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-4">
          <ResultItem 
            label="Итоговая стоимость образования" 
            value={formatCurrency(results.netEducationCost)} 
            color="indigo"
            bold
          />
          
          <ResultItem 
            label="Итоговая сумма накоплений" 
            value={formatCurrency(results.finalSavingsAmount)} 
            color="indigo"
            bold
          />
          
          <ResultItem 
            label={results.savingsGap >= 0 ? "Профицит средств" : "Дефицит средств"} 
            value={formatCurrency(Math.abs(results.savingsGap))} 
            color={results.savingsGap >= 0 ? "green" : "red"}
            bold
          />
        </div>

        {/* Рекомендации */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Рекомендации</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {results.isEnough 
                  ? `Ваших накоплений достаточно для оплаты образования. Вы можете ${results.savingsGap > results.netEducationCost * 0.2 
                    ? 'существенно снизить' 
                    : 'немного уменьшить'} ежемесячный взнос или рассмотреть более престижные учебные заведения.` 
                  : `Для достижения необходимой суммы рекомендуется увеличить ежемесячный взнос до ${formatCurrency(results.adjustedMonthlyContribution)}, или рассмотреть альтернативные варианты образования с меньшей стоимостью.`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;